import { AuthCredentials, AuthRepository, AuthResponse } from '../../domain/repositories/AuthRepository';
import { InvalidCredentialsError, NetworkError, UnknownError } from '../../domain/errors/AuthErrors';

import { AxiosError } from 'axios';
import { TokenService } from '../../application/services/TokenService';
import axios from '@/common/infrastructure/axios';
import { generateRegistrationId } from '../utils/authUtils';

const { VITE_API_BASE_URL, VITE_API_AUTH_URL } = import.meta.env;
const LOGIN_URL = `${VITE_API_BASE_URL}${VITE_API_AUTH_URL}/login`;

export class ApiAuthRepository implements AuthRepository {
  constructor(private readonly tokenService: TokenService) {}

  async login(credentials: AuthCredentials): Promise<AuthResponse> {
    try {
      const { userName, dbName, password, TimezoneName, TimezoneOffset } = credentials;
      const response = await axios.post(LOGIN_URL, {
        DBName: dbName,
        UserName: userName,
        password,
        TimezoneName,
        TimezoneOffset
      });
      const { token } = response.data;

      this.tokenService.setToken(token);
      this.setAuthHeader(token);

      return {
        token,
        user: {
          userName,
          dbName
        }
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401 || error.response?.status === 400) {
          const serverErrorMessage = error.response?.data?.errorMessage;
          if (serverErrorMessage) {
            throw new InvalidCredentialsError(serverErrorMessage);
          }
          throw new InvalidCredentialsError();
        } else if (error.code === 'ECONNABORTED' || !error.response) {
          throw new NetworkError();
        }
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  }

  logout(): void {
    this.tokenService.removeToken();
    this.clearAuthHeader();
  }

  async register(email: string, password: string, firstName: string, lastName: string): Promise<void> {
    try {
      const id = generateRegistrationId();
      await axios.post('/api/account/register', {
        id,
        email,
        password,
        firstName,
        lastName
      });
    } catch (error) {
      if (error instanceof AxiosError && (!error.response || error.code === 'ECONNABORTED')) {
        throw new NetworkError();
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  }

  async resetPassword(_email: string, shouldThrow: boolean = false): Promise<void> {
    // Implement reset password logic with proper error handling
    try {
      // Dummy implementation, used for testing
      if (shouldThrow) {
        throw new Error('Reset failed');
      }
      await Promise.resolve();
    } catch (error) {
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  }

  verifyToken(token: string): boolean {
    return this.tokenService.verifyToken(token);
  }

  private setAuthHeader(token: string): void {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  private clearAuthHeader(): void {
    delete axios.defaults.headers.common.Authorization;
  }
}
